import React, { useEffect, useState, useMemo } from 'react';
import { getSearchParam } from 'utils/params';

const formatTime = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '00:00';
  const totalSec = Math.floor(seconds);
  const hrs = Math.floor(totalSec / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  if (hrs > 0) {
    return `${pad(hrs)}:${pad(min)}:${pad(sec)}`;
  }
  return `${pad(min)}:${pad(sec)}`;
};

const formatISTTime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(date).toUpperCase();
  } catch (e) {
    return '';
  }
};

const Body = () => {
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);

  useEffect(() => {
    let meetingId = getSearchParam('meetingId');

    if (!meetingId) {
      const match = window.location.pathname.match(/\/presentation\/([^/]+)/);
      if (match && match[1]) {
        meetingId = match[1];
      }
      const parts = window.location.pathname.split('/');
      const potentialId = parts[parts.length - 1];
      if (potentialId && potentialId.match(/^[a-z0-9]{40}-[0-9]{13}$/)) {
        meetingId = potentialId;
      }
    }

    if (!meetingId) {
      setLoading(false);
      setError(true);
      return;
    }

    const url = `/presentation/${meetingId}-summary/transcript.json`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setTranscript(data);
        setLoading(false);
      })
      .catch((e) => {
        console.error('Failed to fetch transcript:', e);
        setError(true);
        setLoading(false);
      });
  }, []);

  const filteredSegments = useMemo(() => {
    if (!transcript || !Array.isArray(transcript.segments)) return [];
    return transcript.segments.filter((seg) => {
      if (selectedSpeaker) {
        const segSpeaker = seg.speaker || '';
        const segSpeakers = Array.isArray(seg.speakers) ? seg.speakers : [];
        const matchesSpeaker = segSpeaker.includes(selectedSpeaker) || segSpeakers.includes(selectedSpeaker);
        if (!matchesSpeaker) return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const textMatch = seg.text && seg.text.toLowerCase().includes(query);
        const speakerMatch = seg.speaker && seg.speaker.toLowerCase().includes(query);
        return textMatch || speakerMatch;
      }

      return true;
    });
  }, [transcript, searchQuery, selectedSpeaker]);

  if (loading) return <div className="modal-body">Loading...</div>;
  if (error) return <div className="modal-body">Failed to load transcript.</div>;

  const meetingInfo = transcript && transcript.meeting_info;
  const speakerAnalytics = transcript && Array.isArray(transcript.speaker_analytics) ? transcript.speaker_analytics : [];

  return (
    <div className="modal-body transcript-body">
      {/* Meeting overview banner if available */}
      {meetingInfo && (
        <div className="transcript-meeting-info">
          <div className="info-chips">
            {meetingInfo.meeting_date && (
              <span className="info-chip">📅 {meetingInfo.meeting_date}</span>
            )}
            {meetingInfo.meeting_start_wallclock && (
              <span className="info-chip">
                🕒 Start: {formatISTTime(meetingInfo.meeting_start_wallclock)} IST
              </span>
            )}
            {typeof meetingInfo.total_duration_seconds === 'number' && (
              <span className="info-chip">⏱ Duration: {formatTime(meetingInfo.total_duration_seconds)}</span>
            )}
            {typeof meetingInfo.speech_density_percentage === 'number' && (
              <span className="info-chip">🎙 Speech: {meetingInfo.speech_density_percentage}%</span>
            )}
            {meetingInfo.all_attendees_count && (
              <span className="info-chip">👥 Attendees: {meetingInfo.all_attendees_count}</span>
            )}
          </div>
        </div>
      )}

      {/* Speaker participation breakdown */}
      {speakerAnalytics.length > 0 && (
        <div className="speaker-analytics-section">
          <div className="section-label">Speakers:</div>
          <div className="speaker-chips">
            {speakerAnalytics.map((item, idx) => {
              const isSelected = selectedSpeaker === item.speaker;
              return (
                <button
                  key={idx}
                  type="button"
                  className={`speaker-chip ${isSelected ? 'active' : ''}`}
                  onClick={() => setSelectedSpeaker(isSelected ? null : item.speaker)}
                  title={`Click to filter by ${item.speaker}`}
                >
                  <span className="speaker-chip-name">{item.speaker}</span>
                  {typeof item.percentage_of_meeting === 'number' && (
                    <span className="speaker-chip-percent">{item.percentage_of_meeting}%</span>
                  )}
                </button>
              );
            })}
            {selectedSpeaker && (
              <button
                type="button"
                className="clear-speaker-filter-btn"
                onClick={() => setSelectedSpeaker(null)}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search toolbar */}
      <div className="transcript-toolbar">
        <input
          type="text"
          className="transcript-search-input"
          placeholder="Search transcript text or speaker..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Transcript segments list */}
      {filteredSegments.length > 0 ? (
        <ul className="transcript-segments">
          {filteredSegments.map((seg, index) => {
            const istTime = formatISTTime(seg.real_time_clock);
            const timeLabel = istTime ? `${istTime} IST` : (seg.audio_timestamp || formatTime(seg.start_time_seconds));

            return (
              <li key={index} className="transcript-segment">
                <div className="segment-header">
                  <span className="time-range-badge" title="Spoken at this time (IST)">
                    {timeLabel}
                  </span>
                  {seg.speaker && (
                    <span className="speaker-badge">
                      {seg.speaker}
                    </span>
                  )}
                  {seg.is_overlap && (
                    <span className="overlap-badge" title="Multiple speakers speaking simultaneously">
                      overlap
                    </span>
                  )}
                </div>
                <div className="segment-content">
                  <div className="text">{seg.text}</div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="empty-transcript">
          {searchQuery || selectedSpeaker
            ? 'No transcript segments match your search/filter.'
            : 'No transcript segments available.'}
        </div>
      )}
    </div>
  );
};

export default Body;

